/**
 * Screen Reader Announcer
 * Manages ARIA live regions for screen reader announcements
 * Provides game-specific announcements for educational content
 */

export class ScreenReaderAnnouncer {
  private enabled = false;
  private politeRegion: HTMLDivElement | null = null;
  private assertiveRegion: HTMLDivElement | null = null;
  private statusRegion: HTMLDivElement | null = null;
  private announcementQueue: Array<{ text: string; priority: 'polite' | 'assertive' }> = [];
  private isAnnouncing = false;

  constructor() {
    this.createLiveRegions();
  }

  private createLiveRegions(): void {
    if (typeof document === 'undefined') return;

    // Create container for live regions
    const container = document.createElement('div');
    container.id = 'screen-reader-announcements';
    container.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;

    // Polite region - doesn't interrupt
    this.politeRegion = document.createElement('div');
    this.politeRegion.setAttribute('role', 'status');
    this.politeRegion.setAttribute('aria-live', 'polite');
    this.politeRegion.setAttribute('aria-atomic', 'true');
    this.politeRegion.id = 'sr-polite';

    // Assertive region - interrupts immediately
    this.assertiveRegion = document.createElement('div');
    this.assertiveRegion.setAttribute('role', 'alert');
    this.assertiveRegion.setAttribute('aria-live', 'assertive');
    this.assertiveRegion.setAttribute('aria-atomic', 'true');
    this.assertiveRegion.id = 'sr-assertive';

    // Status region for game state updates
    this.statusRegion = document.createElement('div');
    this.statusRegion.setAttribute('role', 'log');
    this.statusRegion.setAttribute('aria-live', 'polite');
    this.statusRegion.setAttribute('aria-relevant', 'additions');
    this.statusRegion.id = 'sr-status';

    container.appendChild(this.politeRegion);
    container.appendChild(this.assertiveRegion);
    container.appendChild(this.statusRegion);
    document.body.appendChild(container);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // Generic announcement
  announce(text: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.enabled) return;

    this.announcementQueue.push({ text, priority });
    this.processQueue();
  }

  private processQueue(): void {
    if (this.isAnnouncing || this.announcementQueue.length === 0) return;

    const announcement = this.announcementQueue.shift();
    if (!announcement) return;

    this.isAnnouncing = true;
    const region = announcement.priority === 'assertive' ? this.assertiveRegion : this.politeRegion;

    if (region) {
      // Clear first to ensure change is detected
      region.textContent = '';

      // Small delay to ensure screen readers detect the change
      setTimeout(() => {
        region.textContent = announcement.text;

        // Clear after announcement
        setTimeout(() => {
          region.textContent = '';
          this.isAnnouncing = false;
          this.processQueue();
        }, 1000);
      }, 100);
    } else {
      this.isAnnouncing = false;
    }
  }

  // Add to status log (doesn't clear)
  addToLog(text: string): void {
    if (!this.enabled || !this.statusRegion) return;

    const entry = document.createElement('p');
    entry.textContent = text;
    this.statusRegion.appendChild(entry);

    // Keep log manageable
    while (this.statusRegion.children.length > 10) {
      this.statusRegion.removeChild(this.statusRegion.firstChild!);
    }
  }

  // Clear the status log
  clearLog(): void {
    if (this.statusRegion) {
      this.statusRegion.textContent = '';
    }
  }

  // Game-specific announcements

  announceZoomLevel(level: number): void {
    const descriptions: Record<number, string> = {
      1: 'Zoom level 1x. Viewing whole specimen.',
      10: 'Zoom level 10x. Tissue structures visible.',
      40: 'Zoom level 40x. Cell structures becoming visible.',
      100: 'Zoom level 100x. Individual cells visible.',
      400: 'Zoom level 400x. Cell organelles visible.',
      1000: 'Zoom level 1000x. Maximum magnification. Molecular details visible.',
    };

    this.announce(descriptions[level] || `Zoom level ${level}x`);
  }

  announceScanProgress(percent: number): void {
    // Only announce at key thresholds to avoid spam
    const thresholds = [20, 40, 60, 80, 100];
    const roundedPercent = thresholds.find((t) => t >= percent) || 100;

    if (roundedPercent === percent) {
      if (percent === 100) {
        this.announce('Scan complete!', 'assertive');
      } else {
        this.announce(`Scanning: ${percent}% complete`);
      }
    }
  }

  announceScanResult(isLiving: boolean, cellType: string): void {
    const livingStatus = isLiving ? 'Living organism detected' : 'Non-living specimen detected';
    const typeDescription = cellType === 'none'
      ? 'No cells found'
      : `Cell type: ${cellType}`;

    this.announce(`${livingStatus}. ${typeDescription}`, 'assertive');
  }

  announceClassificationResult(correct: boolean, feedback?: string): void {
    if (correct) {
      this.announce(`Correct classification! ${feedback || 'Well done!'}`, 'assertive');
    } else {
      this.announce(`Incorrect classification. ${feedback || 'Try again.'}`, 'assertive');
    }
  }

  announceEvidenceCollected(description: string): void {
    this.announce(`Evidence collected: ${description}`);
    this.addToLog(`Evidence: ${description}`);
  }

  announceLevelStart(levelName: string, objectives: string[]): void {
    const objectivesList = objectives.join('. ');
    this.announce(`Starting level: ${levelName}. Objectives: ${objectivesList}`, 'assertive');
  }

  announceLevelComplete(stars: number, score: number): void {
    this.announce(
      `Level complete! You earned ${stars} stars with a score of ${score} points.`,
      'assertive'
    );
  }

  announceObjectiveProgress(objective: string, current: number, total: number): void {
    if (current === total) {
      this.announce(`Objective complete: ${objective}`);
    } else {
      this.announce(`Progress: ${objective}. ${current} of ${total}.`);
    }
  }

  announceLifeFunction(functionName: string, description: string): void {
    this.announce(`Life function: ${functionName}. ${description}`);
  }

  announceBuilderAction(action: string, cellType?: string): void {
    if (cellType) {
      this.announce(`${action}: ${cellType}`);
    } else {
      this.announce(action);
    }
  }

  announceMenuNavigation(menuItem: string): void {
    this.announce(`Selected: ${menuItem}`);
  }

  announceDialogOpen(title: string): void {
    this.announce(`Dialog opened: ${title}`, 'assertive');
  }

  announceDialogClose(): void {
    this.announce('Dialog closed');
  }

  announceError(message: string): void {
    this.announce(`Error: ${message}`, 'assertive');
  }

  announceSuccess(message: string): void {
    this.announce(`Success: ${message}`, 'assertive');
  }

  // Tutorial/hint announcements
  announceTutorialStep(step: string, instruction: string): void {
    this.announce(`Tutorial step ${step}: ${instruction}`);
  }

  announceHint(hint: string): void {
    this.announce(`Hint: ${hint}`);
  }

  // Clean up
  destroy(): void {
    const container = document.getElementById('screen-reader-announcements');
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    this.announcementQueue = [];
  }
}
